import React, {
  useEffect,
  useState,
  ChangeEvent,
  useCallback,
  useRef, useMemo
} from "react";
import "./User.scss";
import { BusinessPurchaseSection } from './sections/BusinessPurchaseSection';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
  Anchor,
} from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Billing from "./Billing/Billing";
import Places from "./Places/Places";
import Cart from "../Cart/Cart";
import { CartData } from "../../interfaces/CartData";
import { useCart } from "../../services/cartService";
import { ShippingOption } from "../../interfaces/ShippingOption";
import { useHeaderMenu } from "../../contexts/login.context";
import nvCookieHelpers from "../../services/cookieService";
import useLoader from "../../contexts/LoaderContext";
// @ts-ignore
import cartLoader from "../../assets/images/cart_Loader.gif";
import { useTranslation } from "react-i18next";
import { config } from "../../config";
import { formatPhoneNo, isJwtTokenExpired } from "../../helpers/common";
import { initialize } from "@nv-ces/ucp-wrapper";
import { v4 as uuidv4 } from "uuid";
import { PaymentEvents } from "./constants";
import * as intrfce from "./Interfaces";
import { useLocalization } from "../../contexts/LocalizationContext";
import BillingJapan from "./Billing/BillingJapan";
import PlacesJapan from "./Places/PlacesJapan";
import AddressModal from "./AddressModal/AddressModal";
import CheckoutStep2 from "./CheckoutStep2";
import { useCartOperations } from "./hooks/useCartOperations";
import { useAddressOperations } from "./hooks/useAddressOperations";
import {
  createBillingAddressObject,
  createShippingAddressObject,
} from "./utils/addressUtils";
import {
  isObjectEmpty,
  isEmptyAddress,
  createAddressFromCartData,
  savedAddressToFormAddress,
  createEmptyFormAddress,
  createEmptyBillingData,
  updateAddressWithData,
  validateFormFields,
  hasMeaningfulAddressFields,
  handleNewAddressPlaceholder,
  applyUSAddressFieldLimits
} from "./utils";
import { validateField, ValidationContext } from "./validation";
import { useAddressState } from "./hooks/useAddressState";

const User = () => {
  const { userType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const RESET_ADDRESS = "reset";
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderState = queryParams.get("order");
  const stepParam = queryParams.get("step");
  const isVerify: boolean = false;
  const isThanks: boolean = false;
  const [isBusinessPurchase, setIsBusinessPurchase] = useState(false);
  
  // Multi-step checkout state - initialize from query param if present
  const [currentStep, setCurrentStep] = useState<1 | 2>(() => {
    return stepParam === "2" ? 2 : 1;
  });

  const { locale, localeWith_, stateDropdown, countryList, currencySign, countyField, defaultCurrency } = useLocalization();

  // Consolidated address state management using custom hook
  const {
    selectedBillingAddress,
    setSelectedBillingAddress,
    selectedShippingAddress,
    setSelectedShippingAddress,
    savedBillingAddress,
    setSavedBillingAddress,
    savedShippingAddress,
    setSavedShippingAddress,
    formBillingAddress,
    setFormBillingAddress,
    formShippingAddress,
    setFormShippingAddress,
    isBillingReadonly,
    setIsBillingReadonly,
    isShippingReadonly,
    setIsShippingReadonly,
    isBilling,
    setIsBilling,
    temporaryAddress,
    setTemporaryAddress,
    recommendedShippingAddress,
    setRecommendedShippingAddress,
    selectedAddressOption,
    setSelectedAddressOption,
    recommendedBillingAddress,
    setRecommendedBillingAddress,
    temporaryBillingAddress,
    setTemporaryBillingAddress,
    selectedBillingAddressOption,
    setSelectedBillingAddressOption,
    billingAddressDifferences,
    setBillingAddressDifferences,
    shippingAddressDifferences,
    setShippingAddressDifferences,
  } = useAddressState(countryList, locale);
  
  // Ref to track if we're in restricted shipping error state
  const hasRestrictedShippingError = useRef(false);
  // Ref to prevent useEffect from auto-loading addresses right after error
  const skipAddressAutoLoad = useRef(false);
  const [couponCode, setCoupon] = useState<string | null>(null);
  const futureUse = true;
  const [cartData, setCartData] = useState<CartData | undefined>();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
    >({});
  // Track which fields have been touched/blurred to only show errors after user interaction
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [enableReviewOrderBtn, setEnableReviewOrderBtn] =
    useState<boolean>(false);
  const {
    showBodyLoader,
    hideBodyLoader,
    isCartLoading,
    hideCartLoader,
    showCartLoader,
  } = useLoader();
  const [promoErr, setPromoErr] = useState<boolean>(false);
  const [addressErr, setAddressErr] = useState<null | string>(null);
  const [savePayment, setSavePayment] = useState(false);
  const {
    setItemsInCart,
    order,
    setOrder,
    brand,
    setBrand
  } = useHeaderMenu();
  const [paymentEventMessage, setPaymentEventMessage] = useState<string | null>(
    null
  );
  const [validPayment, setValidPayment] = useState<boolean>(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  // State for dual address validation (billing + shipping)
  const [billingValidationResult, setBillingValidationResult] = useState<any>(null);
  const [shippingValidationResult, setShippingValidationResult] = useState<any>(null);

  // Memoize address builder options to avoid recreating on every render
  const addressOptions = useMemo(() => ({
    locale,
    stateDropdown,
    countyField
  }), [locale, stateDropdown, countyField]);

  // Helper function for consistent state value handling across the component
  const getStateValue = useCallback((stateValue: string, fallback?: string) => {
    if (stateDropdown || locale === 'zh-tw') {
      return locale === 'zh-tw' ? "TW" : (stateValue || fallback);
    }
    return fallback;
  }, [stateDropdown, locale]);

  if (userType) {
    sessionStorage.setItem("customer", userType);
  } else {
    // Handle the case when userType is undefined
    navigate(`/${locale}/cart/`);
  }

  const handleLogin = useCallback(() => {
    window.location.href = `${config.bcSSOLoginUrl}${locale}/cart`;
  }, [locale]);

  const reviewOrder = useCallback((user: string) => {
    navigate(`/${locale}/cart/verify/`, { state: { user, validationErrors } });
  }, [locale, navigate, validationErrors]);

  const emptyCart = useCallback((msg: string, pid?: number) => {
    navigate(`/${locale}/cart/empty`, { state: { msg, pid } });
  }, [locale, navigate]);

  const [shippingOptions, setShippingOptions] =
    useState<intrfce.iShippingDetails>({
      shippingOption: 0
    });
  const [shippingList, setShippingList] = useState<ShippingOption[]>([]);
  const [formTaxData, setFormTaxData] = useState<intrfce.iTaxFormData>({
    companyName: "",
    vatNo: "",
  });

  const validateForm = useCallback(
    (
      fieldName: string,
      value: string | {} | boolean,
      formType: "billing" | "shipping" | "",
      skipTouchedCheck: boolean = false // Allow bypassing touched check for form submission
    ): boolean => {
      const validationContext: ValidationContext = {
        locale,
        userType,
        stateDropdown,
        formBillingAddress,
        formShippingAddress,
        formTaxData,
        shippingOptions,
        t,
      };

      const error = validateField(fieldName, value, formType, validationContext);

      // Map form data keys to UI keys for "touched" check and error display (validateFormFields uses form keys e.g. "firstName")
      const shippingFormKeyToErrorKey: Record<string, string> = {
        firstName: "firstNameShiping",
        lastName: "lastNameShiping",
        attn: "attnShiping",
        contact: "contactShiping",
        email: "emailShiping",
        vemail: "vemailShiping",
        line2: "shippingline2",
      };
      const uiErrorKey = formType === "shipping" && shippingFormKeyToErrorKey[fieldName]
        ? shippingFormKeyToErrorKey[fieldName]
        : fieldName;
      // Only set validation errors if field has been touched OR if skipTouchedCheck is true (for form submission)
      const touched = touchedFields.has(fieldName) || touchedFields.has(uiErrorKey);
      const shouldSetError = skipTouchedCheck || touched;

      if (shouldSetError) {
        // Update validation errors in state (use uiErrorKey so form data keys map to UI error keys)
        // For addressBilling and addressShipping, use the fieldName as-is
        if (fieldName === "addressBilling" || fieldName === "addressShipping") {
          // Map to the format expected by the UI: billingaddress or shippingaddress
          const errorFieldName = formType === "billing" ? "billingaddress" : "shippingaddress";
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [errorFieldName]: error,
          }));
        } else if (fieldName === "line2Shipping" || fieldName === "line2Billing" || fieldName === "line2") {
          const errorFieldName = formType === "shipping" ? "shippingline2" : "billingline2";
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [errorFieldName]: error,
          }));
        } else if (
          fieldName === "pincode" ||
          fieldName === "address" ||
          fieldName === "cityValue" ||
          fieldName === "stateValue"
        ) {
          let errorFieldName = formType + fieldName;
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [errorFieldName]: error,
          }));
        }
        if (
          fieldName !== "promoCode" &&
          fieldName !== "addressBilling" &&
          fieldName !== "addressShipping" &&
          fieldName !== "line2Shipping" &&
          fieldName !== "line2Billing" &&
          !(fieldName === "line2" && formType === "shipping")
        ) {
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [uiErrorKey]: error,
          }));
        }
      }

      return error.length === 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formBillingAddress, formShippingAddress, shippingOptions, setValidationErrors, locale, t, userType, formTaxData, touchedFields]
  );
  const orderAccount = useCallback(() => {
    window.location.href = config.bcOrdersUrl;
  }, []);

  const isBillingAddressValid = useCallback((): boolean => {
    // First check if the address has meaningful fields beyond country selection
    if (!hasMeaningfulAddressFields(formBillingAddress, locale)) {
      return false;
    }

    // Validate all fields with locale-specific skip rules
    return validateFormFields(
      formBillingAddress,
      "billing",
      validateForm,
      validationErrors,
      { skipStateValueForNonUS: true, locale }
    );
  }, [formBillingAddress, locale, validateForm, validationErrors]);

  const isBillingFormValid = useCallback((): boolean => {
    return validateFormFields(
      formBillingAddress,
      "billing",
      validateForm,
      validationErrors
    );
  }, [formBillingAddress, validateForm, validationErrors]);

  const isTaxDataValid = useCallback((): boolean => {
    // Only validate tax data if business purchase is checked
    if (!isBusinessPurchase) {
      return true;
    }

    // Check if both fields are valid (no errors)
    if (formTaxData.companyName || formTaxData.vatNo) {
      // If either field has a value, check for errors
      if (validationErrors.companyName || validationErrors.vatNo) {
        return false;
      }
    }
    return true;
  }, [isBusinessPurchase, formTaxData, validationErrors.companyName, validationErrors.vatNo]);

  const isShippingAddressValid = useCallback((): boolean => {
    return validateFormFields(
      formShippingAddress,
      "shipping",
      (fieldName, value, formType) => validateForm(fieldName, value, formType, true),
      validationErrors
    );
  }, [formShippingAddress, validateForm, validationErrors]);

  const isShippingFormValid = useCallback((): boolean => {
    return validateFormFields(
      formShippingAddress,
      "shipping",
      (fieldName, value, formType) => validateForm(fieldName, value, formType, true),
      validationErrors
    );
  }, [formShippingAddress, validateForm, validationErrors]);

  // Check if Continue button should be enabled (Step 1)
  const [isContinueButtonEnabled, setIsContinueButtonEnabled] = useState(false);
  
  useEffect(() => {
    try {
      // Shipping is always required (shown first as primary address)
      const shippingValid = isShippingFormValid() && isShippingAddressValid();
      
      // Billing is only required if different from shipping (checkbox checked)
      const billingValid = !isBilling || (isBillingFormValid() && isBillingAddressValid() && isTaxDataValid());
      
      setIsContinueButtonEnabled(shippingValid && billingValid);
    } catch (error) {
      console.error('Error checking button enabled state:', error);
      setIsContinueButtonEnabled(false);
    }
  }, [
    formBillingAddress,
    formShippingAddress,
    formTaxData,
    isBusinessPurchase,
    isBilling,
    locale,
    isShippingFormValid,
    isShippingAddressValid,
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    validationErrors
  ]);

  const handleError = useCallback((str: string, pid?: number) => {
    if (pid) {
      if (str && str.length) {
        emptyCart(str, pid);
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"), pid);
      }
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
    setItemsInCart(0);
    hideBodyLoader();
  }, [emptyCart, t, hideBodyLoader, setItemsInCart]);
  const handleCouponError = useCallback((str: string) => {
    if (str && str.length) {
      emptyCart(str);
    }
    if (couponCode && couponCode.length >= 0) {
      setPromoErr(true);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: t("INVALID_PROMO"),
      }));
    } else if (!couponCode) {
      setPromoErr(true);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: t("INVALID_PROMO"),
      }));
    } else {
      setPromoErr(false);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: "",
      }));
    }
    hideBodyLoader();
  }, [couponCode, emptyCart, hideBodyLoader, t]);
  const { getCart, updateCart, submitCart, submitAddress } =
    useCart();

  const fetchCart = async () => {
    showBodyLoader();
    await getCart(handleSuccess, handleError, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const fieldNameRef = useRef<string | null>(null);
  const handleBillingInputChange = (
    fieldName: string,
    value: string | boolean
  ) => {
    fieldNameRef.current = fieldName;

    // Map billing-specific field names to generic field names
    const fieldMap: Record<string, keyof intrfce.iAddressFormData> = {
      firstNameBilling: 'firstName',
      lastNameBilling: 'lastName',
      contactBilling: 'contact',
      emailBilling: 'email',
      vemailBilling: 'vemail'
    };

    const mappedFieldName = fieldMap[fieldName] || fieldName;

    // Update formBillingAddress with the mapped field name
    setFormBillingAddress((prevData) => ({ ...prevData, [mappedFieldName]: value }));

    // Always validate on change to clear errors when field becomes valid
    // The validateForm function will only show errors if field has been touched
    validateForm(fieldName, value, "billing");
  };

  // Handle billing field blur - mark as touched and validate
  const handleBillingFieldBlur = (fieldName: string, value: string | boolean) => {
    setTouchedFields((prev) => {
      const updated = new Set(prev);
      updated.add(fieldName);
      return updated;
    });
    // Use skipTouchedCheck since we know the field is being touched
    validateForm(fieldName, value, "billing", true);
  };

  const ucpInitialized = useRef(false);
  const isLoggedIn =
    nvCookieHelpers.getCookie("isUserLoggedIn") === "true" ? true : false;

  // Create refs to store form field values
  const formFieldsRef = useRef<{
    billing: intrfce.iAddressFormData | null;
    shipping: intrfce.iAddressFormData | null;
  }>({
    billing: null,
    shipping: null,
  });

  // Handle step query parameter changes (e.g., when returning from review page)
  useEffect(() => {
    const stepParam = queryParams.get("step");
    if (stepParam === "2" && currentStep !== 2) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (stepParam === "1" && currentStep !== 1) {
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Monitor page visibility to detect when page becomes active again
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When page becomes visible again, check if token is expired
      if (!document.hidden && cartData?.idt) {
        if (isJwtTokenExpired(cartData.idt)) {
          console.warn("JWT token (idt) is expired after page became active. Refreshing page to reinitialize.");
          window.location.reload();
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cartData?.idt]);

  // Initialize UCP without state updates
  useEffect(() => {
    if (!ucpInitialized.current && cartData) {
      // Check if idt token exists and is expired
      if (cartData?.idt && isJwtTokenExpired(cartData.idt)) {
        console.warn("JWT token (idt) is expired. Refreshing page to reinitialize.");
        // Refresh the page to reinitialize everything with a fresh token
        window.location.reload();
        return;
      }

      ucpInitialized.current = true;
      let originEnvironment = window.location.origin
        .split("/")[2]
        .split("-")[0]
        ?.toUpperCase();
      originEnvironment =
        originEnvironment !== "STG" && originEnvironment !== "DEV"
          ? "PROD"
          : originEnvironment;

      // Store initial form values in ref
      formFieldsRef.current = {
        billing: formBillingAddress,
        shipping: formShippingAddress,
      };
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        paymentSrc: '',
      }));
      initialize({
        version: "v1",
        environment: originEnvironment as any,
        clientId: "nvstore",
      })
        .then((Ucp) => {
          if (isLoggedIn && cartData?.idt) {
            window.Ucp?.events?.addEventListener(
              "event-registered-user-payment-methods",
              (event: any) => {
                if (event.detail.message === PaymentEvents.Exists || event.detail.message === PaymentEvents.Changed) {
                  setValidPayment(true);
                  setEnableReviewOrderBtn(true);
                } else if (event.detail.message === PaymentEvents.NotExists) {
                  setEnableReviewOrderBtn(false);
                } else if (event.detail.message === PaymentEvents.Changed) {
                  setEnableReviewOrderBtn(false);
                }
              }
            );
          }
          window.Ucp?.events?.addEventListener(
            "event-guest-payment-method",
            (event: any) => {
              setPaymentEventMessage(event.detail.message);
              if (event.detail.message === PaymentEvents.Valid || event.detail.message === PaymentEvents.Saved) {
                setValidPayment(true);
              }
              switch (event.detail.message) {
                case PaymentEvents.Valid:
                  setSavePayment(false);
                  setEnableReviewOrderBtn(true);
                  break;
                case PaymentEvents.Invalid:
                case PaymentEvents.MissingInput:
                default:
                  setSavePayment(false);
                  setEnableReviewOrderBtn(false);
                  break;
              }
            }
          );

          let initEnvironment =
            originEnvironment === "DEV"
              ? `${originEnvironment}_UE1`
              : originEnvironment;

          const initOptions = {
            ...((!cartData?.idt || !isLoggedIn) && {
              guestSessionId: cartData?.paymentSession?.id,
            }),
            ...(cartData?.idt && isLoggedIn && { token: cartData?.idt }),
            environment: initEnvironment as EnvironmentAPI,
            theme: "light" as Theme,
            nonce: uuidv4(),
            locale: localeWith_,
            currency: defaultCurrency.toLowerCase(),
            clientFlavor: "public",

          };
          Ucp.initialize(initOptions);
        })
        .catch((error) => {
          console.error("Failed to initialize UCP:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, cartData?.paymentSession?.id, cartData?.idt, isLoggedIn]);

  // Handle form state updates based on payment event
  useEffect(() => {
    if (paymentEventMessage === PaymentEvents.Saved && formFieldsRef.current) {
      const { billing, shipping } = formFieldsRef.current;

      if (billing) {
        setFormBillingAddress((prevState) => ({
          ...prevState,
          ...billing,
        }));
      }

      if (shipping) {
        setFormShippingAddress((prevState) => ({
          ...prevState,
          ...shipping,
        }));
      }

      submitCustomerForm();
      setPaymentEventMessage(null); // Reset the event message
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentEventMessage]);

  // Define functions that are used by hooks (must be before hook calls)
  const handleSavedBillingAddressChange = useCallback((
    fieldName: string,
    selectedValue: any,
    addrArr?: any
  ) => {
    console.log("handleSavedBillingAddressChange==>", selectedValue);
    if (selectedValue === "reset") {
      // Clear only billing-related validation errors when adding new billing address
      setValidationErrors((prevErrors) => {
        const {
          firstNameBilling,
          lastNameBilling,
          emailBilling,
          vemailBilling,
          contactBilling,
          billingaddress,
          addressBilling,
          cityBilling,
          stateNameBilling,
          zipBilling,
          countryBilling,
          addressErr,
          ...restErrors
        } = prevErrors;
        return restErrors;
      });
      setAddressErr("");

      setIsBillingReadonly(false);
      setFormBillingAddress(createEmptyFormAddress(countryList));
      setFormBillingAddress((prev) => ({
        ...prev,
        ...createEmptyBillingData(cartData?.emailAddress)
      }));

      // Create and select the new address placeholder immediately
      handleNewAddressPlaceholder(
        selectedValue,
        savedBillingAddress,
        (addresses) => {
          setSavedBillingAddress(addresses);
          // If shipping is disabled, sync shipping addresses with billing
          if (!isBilling) {
            setSavedShippingAddress(addresses);
          }
        },
        (newAddress) => {
          setSelectedBillingAddress(newAddress);
        },
        addressOptions,
        t("ADD_NEW_ADDRESS")
      );
      // When billing and shipping are the same (!isBilling), shipping is the source of truth
      // So we should NOT update shipping from billing when resetting billing
    } else {
      const addresses = addrArr ? addrArr : savedBillingAddress;
      const selectedAddress = addresses?.filter(
        (bill) => bill.id === Number(selectedValue)
      );

      if (selectedAddress && selectedAddress.length > 0) {
        let updatedAddress = selectedAddress[0];
        setSelectedBillingAddress(updatedAddress);
        console.log("handleSavedBillingAddressChange==>", fieldName);

        if (updatedAddress) {
          const formAddress = savedAddressToFormAddress(updatedAddress, addressOptions);
          
          // For "Add New Address" (id === -1), only clear if the address is truly empty
          // Otherwise, preserve existing form data or use address data if available
          if (Number(updatedAddress.id) === -1) {
            // Check if address has meaningful data
            const hasAddressData = updatedAddress.line1 || updatedAddress.city || updatedAddress.postalCode;
            
            if (hasAddressData) {
              // Address has data - use it
              setFormBillingAddress({
                ...formAddress,
                id: -1,
                firstName: updatedAddress?.firstName || formAddress.firstName || "",
                lastName: updatedAddress?.lastName || formAddress.lastName || "",
                contact: updatedAddress?.phoneNumber || formAddress.contact || "",
                // For logged-in users, email comes from cartData.emailAddress (root level)
                email: (userType === "loggedIn" && cartData?.emailAddress)
                  ? cartData.emailAddress
                  : (formAddress.email || ""),
                vemail: (userType === "loggedIn" && cartData?.emailAddress)
                  ? cartData.emailAddress
                  : (formAddress.vemail || "")
              });
            } else {
              // Address is empty placeholder - preserve existing form data, just make it editable
              setFormBillingAddress((prev) => ({
                ...prev,
                id: -1,
                // Keep existing data, just ensure it's editable
              }));
            }
          } else {
            // For saved addresses (id > 0), use the address data
            setFormBillingAddress({
              ...formAddress,
              ...(updatedAddress.id ? { id: Number(updatedAddress.id) } : {}),
              firstName: updatedAddress?.firstName || formAddress.firstName || "",
              lastName: updatedAddress?.lastName || formAddress.lastName || "",
              contact: updatedAddress?.phoneNumber || formAddress.contact || "",
              // For logged-in users, email comes from cartData.emailAddress (root level)
              email: (userType === "loggedIn" && cartData?.emailAddress)
                ? cartData.emailAddress
                : (formAddress.email || ""),
              vemail: (userType === "loggedIn" && cartData?.emailAddress)
                ? cartData.emailAddress
                : (formAddress.vemail || "")
            });
          }

          // Set readonly based on address id: -1 (new address) is editable, saved addresses (id > 0) are readonly for logged-in users
          if (Number(updatedAddress.id) === -1) {
            setIsBillingReadonly(false);
          } else {
            setIsBillingReadonly(userType === "loggedIn");
          }

          // Validate all billing fields
          // Only validate billing contact fields (not address fields)
          const billingContactFields = ['firstNameBilling', 'lastNameBilling', 'contactBilling', 'emailBilling', 'vemailBilling'];
          billingContactFields.forEach((fieldName) => {
            const fieldMap: Record<string, keyof intrfce.iAddressFormData> = {
              firstNameBilling: 'firstName',
              lastNameBilling: 'lastName',
              contactBilling: 'contact',
              emailBilling: 'email',
              vemailBilling: 'vemail'
            };
            const mappedField = fieldMap[fieldName];
            if (mappedField && formBillingAddress[mappedField]) {
              validateForm(fieldName, formBillingAddress[mappedField], "billing");
            }
          });
        }
      }
    }
  }, [
    savedBillingAddress,
    formBillingAddress,
    cartData?.emailAddress,
    userType,
    countryList,
    addressOptions,
    setValidationErrors,
    setAddressErr,
    setIsBillingReadonly,
    setFormBillingAddress,
    setSelectedBillingAddress,
    validateForm,
    isBilling,
    setSavedBillingAddress,
    setSavedShippingAddress,
    t
  ]);

  const handleSavedShippingAddressChange = useCallback((
    fieldName: string,
    selectedValue: any,
    addrArr?: any
  ) => {
    console.log("handleSavedShippingAddressChange==>", selectedValue);

    // Clear shippingAddressErr when user manually selects a saved address (only if we're in error state)
    if (validationErrors.shippingAddressErr && hasRestrictedShippingError.current) {
      console.log("Clearing shippingAddressErr - user selected saved address");
      setValidationErrors((prevErrors) => {
        const { shippingAddressErr, ...restErrors } = prevErrors;
        return restErrors;
      });
      setEnableReviewOrderBtn(true);
      hasRestrictedShippingError.current = false;
    }

    if (selectedValue === "reset") {
      // Clear only shipping-related validation errors when adding new shipping address
      setValidationErrors((prevErrors) => {
        const {
          firstNameShiping,
          lastNameShiping,
          attnShiping,
          contactShiping,
          shippingaddress,
          addressShipping,
          cityShipping,
          stateNameShipping,
          zipShipping,
          countryShipping,
          shippingAddressErr,
          ...restErrors
        } = prevErrors;
        return restErrors;
      });

      setIsShippingReadonly(false);
      // Clear addressRef to ensure Places component doesn't restore old values
      addressRef.current = { formType: "shipping", data: null };

      // Clear form fields
      setFormShippingAddress({
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: "",
        countryName: "",
        countryValue: "",
        firstName: "",
        lastName: "",
        attn: "",
        contact: "",
        email: "",
        vemail: "",
      });

      // If billing is not separate, also clear billing
      if (!isBilling) {
        handleSavedBillingAddressChange("savedBillingAddress", selectedValue);
      }

      // Create and select the new address placeholder immediately
      handleNewAddressPlaceholder(
        selectedValue,
        savedShippingAddress,
        setSavedShippingAddress,
        (newAddress) => {
          setSelectedShippingAddress(newAddress);
        },
        addressOptions,
        t("ADD_NEW_ADDRESS")
      );
    } else {
      const addresses = addrArr ? addrArr : savedShippingAddress;
      const selectedAddress = addresses?.filter(
        (ship) => ship.id === Number(selectedValue)
      );

      if (selectedAddress && selectedAddress.length > 0) {
        const updatedAddress = selectedAddress[0];
        setSelectedShippingAddress(updatedAddress);

        if (updatedAddress) {
          const formAddress = savedAddressToFormAddress(updatedAddress, addressOptions);
          
          // For "Add New Address" (id === -1), only clear if the address is truly empty
          // Otherwise, preserve existing form data or use address data if available
          if (Number(updatedAddress.id) === -1) {
            // Check if address has meaningful data
            const hasAddressData = updatedAddress.line1 || updatedAddress.city || updatedAddress.postalCode;
            
            if (hasAddressData) {
              // Address has data - use it; apply US char limits for saved/cart data
              setFormShippingAddress(applyUSAddressFieldLimits({
                ...formAddress,
                firstName: updatedAddress?.firstName || "",
                lastName: updatedAddress?.lastName || "",
                attn: updatedAddress?.attn || "",
                contact: updatedAddress?.phoneNumber || "",
                // For logged-in users, email comes from cartData.emailAddress (root level)
                email: (userType === "loggedIn" && cartData?.emailAddress)
                  ? cartData.emailAddress
                  : (formAddress.email || ""),
                vemail: (userType === "loggedIn" && cartData?.emailAddress)
                  ? cartData.emailAddress
                  : (formAddress.vemail || "")
              }));
            } else {
              // Address is empty placeholder - preserve existing form data, just make it editable
              setFormShippingAddress((prev) => ({
                ...prev,
                // Keep existing data, just ensure it's editable
              }));
            }
          } else {
            // For saved addresses (id > 0), use the address data; apply US char limits for saved addresses
            setFormShippingAddress(applyUSAddressFieldLimits({
              ...formAddress,
              firstName: updatedAddress?.firstName || "",
              lastName: updatedAddress?.lastName || "",
              attn: updatedAddress?.attn || "",
              contact: updatedAddress?.phoneNumber || "",
              // For logged-in users, email comes from cartData.emailAddress (root level)
              email: (userType === "loggedIn" && cartData?.emailAddress)
                ? cartData.emailAddress
                : (formAddress.email || ""),
              vemail: (userType === "loggedIn" && cartData?.emailAddress)
                ? cartData.emailAddress
                : (formAddress.vemail || "")
            }));
          }
        }

        // Set readonly based on address id: -1 (new address) is editable, saved addresses (id > 0) are readonly for logged-in users
        if (Number(updatedAddress.id) === -1) {
          setIsShippingReadonly(false);
        } else {
          setIsShippingReadonly(userType === "loggedIn");
        }
      }
    }
  }, [validationErrors.shippingAddressErr, savedShippingAddress, addressOptions, userType, t, setValidationErrors, setEnableReviewOrderBtn, setIsShippingReadonly, setFormShippingAddress, setSelectedShippingAddress, setSavedShippingAddress, isBilling, handleSavedBillingAddressChange, cartData?.emailAddress]);

  // Initialize consolidated hooks - only get shared functionality
  const { handleSuccess } = useCartOperations({
    orderState,
    cartData,
    currencySign,
    locale,
    countyField,
    stateDropdown,
    userType,
    savedBillingAddress,
    savedShippingAddress,
    formBillingAddress,
    formFieldsRef,
    setBrand,
    setFormTaxData,
    setIsBusinessPurchase,
    setCartData,
    setShippingList,
    setShippingOptions,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setFormBillingAddress,
    setFormShippingAddress,
    setSelectedBillingAddress,
    setIsBillingReadonly,
    setSelectedShippingAddress,
    setIsBilling,
    setIsShippingReadonly,
    setPromoErr,
    setValidationErrors,
    fetchCart,
    emptyCart,
    hideBodyLoader,
    showBodyLoader,
    handleSavedBillingAddressChange,
    updateCart,
    reviewOrder,
    getStateValue,
    orderAccount,
    t
  });

  const {
    handleConfirmAddress,
    handleShippingAddressUpdate,
    handleAddressOptionChange,
    handleContinueToPayment,
    handleBackToAddresses
  } = useAddressOperations({
    isBilling,
    currentStep,
    formBillingAddress,
    formShippingAddress,
    recommendedBillingAddress,
    recommendedShippingAddress,
    selectedBillingAddressOption,
    selectedAddressOption,
    selectedBillingAddress,
    selectedShippingAddress,
    locale,
    countryList,
    brand,
    setFormBillingAddress,
    setFormShippingAddress,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setSelectedBillingAddress,
    setSelectedShippingAddress,
    setRecommendedBillingAddress,
    setRecommendedShippingAddress,
    setBillingAddressDifferences,
    setShippingAddressDifferences,
    setShowAddressModal,
    setCurrentStep,
    setSelectedAddressOption,
    setValidationErrors,
    setBillingValidationResult,
    setShippingValidationResult,
    setTouchedFields,
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isShippingFormValid,
    isShippingAddressValid,
    validateForm,
    submitAddress,
    showBodyLoader,
    hideBodyLoader,
    t
  });

  // Update ref when form values change
  useEffect(() => {
    if (formBillingAddress) {
      if (!isObjectEmpty(formBillingAddress)) {
        formFieldsRef.current.billing = formBillingAddress;
      }
    }
  }, [formBillingAddress]);

  useEffect(() => {
    if (formShippingAddress) {
      if (!isObjectEmpty(formShippingAddress)) {
        formFieldsRef.current.shipping = formShippingAddress;
      }
    }
  }, [formShippingAddress]);

  useEffect(() => {
    // This will be executed after the state has been updated
    let fieldName = fieldNameRef.current;
    if (fieldName) {
      // Map field name if it's a billing contact field
      const fieldMap: Record<string, keyof intrfce.iAddressFormData> = {
        firstNameBilling: 'firstName',
        lastNameBilling: 'lastName',
        contactBilling: 'contact',
        emailBilling: 'email',
        vemailBilling: 'vemail'
      };
      const mappedFieldName = fieldMap[fieldName];
      const value = mappedFieldName 
        ? formBillingAddress[mappedFieldName]
        : formBillingAddress[fieldName as keyof typeof formBillingAddress];
      
      // Only validate if field has a value (to avoid showing errors on empty fields)
      // Full validation will happen on blur
      if (value && typeof value === "string" && value.trim().length > 0) {
        validateForm(fieldName, value, "billing");
      }
    }
    // Ensure selectedBillingAddress is of type iSavedAddress before accessing properties
    // Update billing address contact fields when shipping address changes (same address)
    if (!isBilling && (selectedShippingAddress === RESET_ADDRESS || !selectedShippingAddress || selectedShippingAddress === null)) {
      setFormBillingAddress((prev) => ({
        ...prev,
        firstName: formShippingAddress.firstName || "",
        lastName: formShippingAddress.lastName || "",
        contact: formShippingAddress.contact || "",
        email: formBillingAddress.email || "", // Preserve email
        vemail: formBillingAddress.vemail || "", // Preserve vemail
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateForm, isBilling, formBillingAddress, selectedBillingAddress, setFormShippingAddress]);

  useEffect(() => {
    // This will be executed after the state has been updated
    // Only validate if field has been touched (user has interacted with it)
    let fieldName = fieldNameRef.current;
    // Skip validation for companyName and vatNo here - they're handled separately
    if (fieldName && fieldName !== "companyName" && fieldName !== "vatNo") {
      // Only validate if field has been touched
      if (touchedFields.has(fieldName)) {
        validateForm(fieldName, formTaxData[fieldName], "billing");
      }
    }
  }, [formTaxData, validateForm, touchedFields]);
  // Separate useEffect to handle interdependent validation of company name and VAT number
  useEffect(() => {
    if (!isBusinessPurchase) {
      // Clear errors if business purchase is unchecked
      setValidationErrors((prev) => ({
        ...prev,
        companyName: '',
        vatNo: '',
      }));
      return;
    }

    let companyNameError = '';
    let vatNoError = '';

    // If VAT number is filled, company name is required
    if (formTaxData.vatNo && formTaxData.vatNo.trim() !== '') {
      if (!formTaxData.companyName || formTaxData.companyName.trim() === '') {
        companyNameError = t("COMPANY_NAME_REQUIRED");
      }
    }

    // If company name is filled, VAT number is required
    if (formTaxData.companyName && formTaxData.companyName.trim() !== '') {
      if (!formTaxData.vatNo || formTaxData.vatNo.trim() === '') {
        vatNoError = t("VAT_NUMBER_REQUIRED");
      }
    }

    // Update validation errors
    setValidationErrors((prev) => ({
      ...prev,
      companyName: companyNameError,
      vatNo: vatNoError,
    }));
  }, [formTaxData.companyName, formTaxData.vatNo, isBusinessPurchase, t]);

  useEffect(() => {
    // Skip auto-loading addresses if we're in restricted shipping error state
    if (skipAddressAutoLoad.current) {
      console.log("Skipping address auto-load due to error state");
      return;
    }
    
    let isNewBillingAddressExists: intrfce.iSavedAddress | undefined;
    let isNewShippingAddressExists: intrfce.iSavedAddress | undefined;
    let isBillingAddressEmpty: boolean = false;
    let updatedBillingAddresses: intrfce.iSavedAddress[] | undefined;
    let updatedShippingAddresses: intrfce.iSavedAddress[] | undefined;
    let shippingAddressArray: intrfce.iSavedAddress[];
    let billingAddressArray: intrfce.iSavedAddress[];
    let newBillingAddressId: any;
    let newShippingAddressId: any;
    let defaultBillingAddress: any;
    let defaultShippingAddress: any;
    let newShippingAddress: any;
    let newBillingAddress: any;
    console.log("selectedBillingAddress==>", selectedBillingAddress);
    if (typeof selectedBillingAddress !== "string") {
      isBillingAddressEmpty = isEmptyAddress(selectedBillingAddress, t("ADD_NEW_ADDRESS"));
    }
    if (typeof selectedShippingAddress !== "string") {
      isBillingAddressEmpty = isEmptyAddress(selectedShippingAddress, t("ADD_NEW_ADDRESS"));
    }

    if (
      savedBillingAddress &&
      savedBillingAddress.length &&
      savedShippingAddress &&
      savedShippingAddress.length
    ) {
      billingAddressArray = updatedBillingAddresses?.length
        ? updatedBillingAddresses
        : savedBillingAddress;
      shippingAddressArray = updatedShippingAddresses?.length
        ? updatedShippingAddresses
        : savedShippingAddress;

      isNewBillingAddressExists = billingAddressArray.find(
        (addr) => Number(addr.id) <= 0
      );
      isNewShippingAddressExists = shippingAddressArray.find(
        (addr) => Number(addr.id) <= 0
      );
      if (
        typeof selectedBillingAddress === "string" ||
        typeof selectedShippingAddress === "string"
      ) {
        // Handle billing address placeholder
        newBillingAddressId = handleNewAddressPlaceholder(
          selectedBillingAddress,
          savedBillingAddress,
          (addresses) => {
            setSavedBillingAddress(addresses);
            // If shipping is disabled, sync shipping addresses with billing
            if (!isBilling) {
              setSavedShippingAddress(addresses);
            }
          },
          setSelectedBillingAddress,
          addressOptions,
          t("ADD_NEW_ADDRESS")
        );

        // Handle shipping address placeholder if shipping is enabled
        if (isBilling) {
          newShippingAddressId = handleNewAddressPlaceholder(
            selectedShippingAddress,
            savedShippingAddress,
            setSavedShippingAddress,
            (address) => {
              setSelectedShippingAddress(address);
              // When placeholder is created, ensure form is cleared and readonly is false
              if (address && address.id === -1) {
                setFormShippingAddress({
                  pincode: "",
                  address: "",
                  line2: "",
                  cityValue: "",
                  stateValue: "",
                  countryName: "",
                  countryValue: "",
                  firstName: "",
                  lastName: "",
                  attn: "",
                  contact: "",
                  email: "",
                  vemail: "",
                });
                setIsShippingReadonly(false);
                addressRef.current = { formType: "shipping", data: null };
              }
            },
            addressOptions,
            t("ADD_NEW_ADDRESS")
          );
        }
      }
      if (cartData &&
        (cartData.billingAddress?.id === -1 ||
          cartData.shippingAddress?.id === -1 ||
          cartData.billingAddress?.id === "0" ||
          cartData.shippingAddress?.id === "0")
      ) {
        if (isBilling) {
          if (
            !isNewShippingAddressExists &&
            !isBillingAddressEmpty &&
            !(Number(cartData.shippingAddress?.id) > 0)
          ) {
            newShippingAddress = createAddressFromCartData(
              cartData.shippingAddress,
              addressOptions,
              "Order Address"
            );
            updatedShippingAddresses = [
              newShippingAddress,
              ...savedShippingAddress,
            ];
            setSavedShippingAddress(updatedShippingAddresses);
            newShippingAddressId = newShippingAddress.id;
          } else {
            if (
              typeof selectedShippingAddress !== "string" &&
              selectedShippingAddress?.id !==
              Number(cartData.shippingAddress.id)
            ) {
              newShippingAddress = shippingAddressArray.find(
                (addr) => addr.id === Number(selectedShippingAddress?.id)
              );
            } else {
              newShippingAddress = shippingAddressArray.find(
                (addr) => addr.id === Number(cartData.shippingAddress?.id)
              );
            }
            if (newShippingAddress && !isBillingAddressEmpty) {
              newShippingAddressId = isBilling
                ? newShippingAddress.id
                : (typeof selectedBillingAddress !== "string" && (selectedBillingAddress as intrfce.iSavedAddress)?.id);
            } else {
              newShippingAddressId = "reset";
            }
          }
        }

        if (
          !isNewBillingAddressExists &&
          !isBillingAddressEmpty &&
          !(Number(cartData.billingAddress?.id) > 0)
        ) {
          newBillingAddress = createAddressFromCartData(
            cartData.billingAddress,
            addressOptions,
            "Order Address"
          );
          updatedBillingAddresses = [newBillingAddress, ...savedBillingAddress];
          setSavedBillingAddress(updatedBillingAddresses);
          if (!isBilling) {
            updatedShippingAddresses = [
              newBillingAddress,
              ...savedShippingAddress,
            ];
            setSavedShippingAddress(updatedShippingAddresses);
          }
          newBillingAddressId = newBillingAddress.id;
        } else {
          if (
            typeof selectedBillingAddress !== "string" &&
            selectedBillingAddress?.id !== Number(cartData.billingAddress.id)
          ) {
            newBillingAddress = billingAddressArray.find(
              (addr) => addr.id === Number(selectedBillingAddress?.id)
            );
          } else {
            newBillingAddress = billingAddressArray.find(
              (addr) => addr.id === Number(cartData.billingAddress?.id)
            );
          }
          if (newBillingAddress && !isBillingAddressEmpty) {
            newBillingAddressId = newBillingAddress.id;
          } else {
            newBillingAddressId = "reset";
          }
        }
      } else if (isBillingAddressEmpty || isBillingAddressEmpty) {
        console.log(selectedBillingAddress);
        if (isBillingAddressEmpty) {
          newBillingAddressId = "reset";
        } else {
          if (typeof selectedBillingAddress !== "string") {
            newBillingAddressId = selectedBillingAddress?.id;
          }
        }
        if (isBillingAddressEmpty) {
          newShippingAddressId = "reset";
        } else {
          if (typeof selectedShippingAddress !== "string") {
            newShippingAddressId = selectedShippingAddress?.id;
          }
        }
      } else if (
        typeof selectedBillingAddress !== "string" &&
        typeof selectedShippingAddress !== "string" &&
        selectedShippingAddress?.id !== undefined &&
        selectedShippingAddress?.id !== null &&
        selectedBillingAddress?.id !== undefined &&
        selectedBillingAddress?.id !== null
      ) {
        if (
          isBilling &&
          typeof selectedShippingAddress !== "string" &&
          selectedShippingAddress.id
        ) {
          isNewShippingAddressExists = shippingAddressArray.find(
            (addr) => Number(addr.id) === selectedShippingAddress?.id
          );
          newShippingAddressId = isNewShippingAddressExists?.id;
        } else {
          newShippingAddressId = selectedShippingAddress?.id;
        }
        if (
          typeof selectedBillingAddress !== "string" &&
          selectedBillingAddress.id === formBillingAddress?.id
        ) {
          isNewBillingAddressExists = billingAddressArray.find(
            (addr) => Number(addr.id) === selectedBillingAddress?.id
          );
          newBillingAddressId = isNewBillingAddressExists?.id;
        } else {
          newBillingAddressId = selectedBillingAddress?.id;
        }
      } else {
        isNewBillingAddressExists = billingAddressArray.find(
          (addr) => addr.id === Number(cartData?.billingAddress?.id)
        );
        if (!isNewBillingAddressExists) {
          isNewBillingAddressExists = billingAddressArray.find(
            (addr) => addr.id === -1
          );
          if (!isNewBillingAddressExists && selectedBillingAddress) {
            isNewBillingAddressExists = billingAddressArray.find(
              (addr: intrfce.iSavedAddress) => addr.id === Number((selectedBillingAddress as intrfce.iSavedAddress).id)
            );
          }
        }
        newBillingAddressId = isNewBillingAddressExists?.id;

        isNewShippingAddressExists = shippingAddressArray.find(
          (addr) => addr.id === Number(cartData?.shippingAddress?.id)
        );
        if (!isNewShippingAddressExists) {
          isNewShippingAddressExists = shippingAddressArray.find(
            (addr) => addr.id === -1
          );
          if (!isNewShippingAddressExists && selectedShippingAddress) {
            isNewShippingAddressExists = shippingAddressArray.find(
              (addr: intrfce.iSavedAddress) => addr.id === Number((selectedShippingAddress as intrfce.iSavedAddress).id)
            );
          }
        }
        newShippingAddressId = isNewShippingAddressExists?.id;
      }
    }

    // Billing Address Logic
    if (
      savedBillingAddress &&
      savedBillingAddress.length &&
      userType === "loggedIn"
    ) {
      defaultBillingAddress = savedBillingAddress.find(
        (addr) => addr.isDefault
      );
      const billingAddressIdToUse =
        newBillingAddressId !== undefined
          ? newBillingAddressId
          : defaultBillingAddress?.id;
      
      // Skip restoration if user explicitly selected "Add New Address" (reset or id === -1)
      const isUserSelectedNewAddress = 
        selectedBillingAddress === "reset" ||
        (typeof selectedBillingAddress !== "string" && selectedBillingAddress?.id === -1);
      
      if (
        billingAddressIdToUse !== undefined &&
        billingAddressIdToUse !== "reset" &&
        typeof selectedBillingAddress !== "string" &&
        selectedBillingAddress?.id !== billingAddressIdToUse &&
        !isUserSelectedNewAddress // Don't restore if user explicitly selected new address
      ) {
        handleSavedBillingAddressChange(
          "savedBillingAddress",
          billingAddressIdToUse,
          updatedBillingAddresses
        );
      }
    }

    // Shipping Address Logic
    if (
      savedShippingAddress &&
      savedShippingAddress.length &&
      userType === "loggedIn"
    ) {
      defaultShippingAddress = savedShippingAddress.find(
        (addr) => addr.isDefault
      );
      const shippingAddressIdToUse =
        newShippingAddressId !== undefined
          ? newShippingAddressId
          : defaultShippingAddress?.id;
      
      // Skip restoration if user explicitly selected "Add New Address" (reset or id === -1)
      const isUserSelectedNewAddress = 
        selectedShippingAddress === "reset" ||
        (typeof selectedShippingAddress !== "string" && selectedShippingAddress?.id === -1);
      
      if (
        shippingAddressIdToUse !== undefined &&
        typeof selectedShippingAddress !== "string" &&
        shippingAddressIdToUse !== "reset" &&
        selectedShippingAddress?.id !== shippingAddressIdToUse &&
        !isUserSelectedNewAddress // Don't restore if user explicitly selected new address
      ) {
        // If it's a new address (id 0 or -1), ensure fields are cleared and readonly is false
        if (Number(shippingAddressIdToUse) === 0 || Number(shippingAddressIdToUse) === -1) {
          setFormShippingAddress({
            pincode: "",
            address: "",
            line2: "",
            cityValue: "",
            stateValue: "",
            countryName: "",
            countryValue: "",
            firstName: "",
            lastName: "",
            attn: "",
            contact: "",
            email: "",
            vemail: "",
          });
          setIsShippingReadonly(false);
        }
        handleSavedShippingAddressChange(
          "savedShippingAddress",
          shippingAddressIdToUse,
          updatedShippingAddresses
        );
      }
    }

    // If isBilling is false, use the same logic for billing and shipping
    if (
      !isBilling &&
      savedBillingAddress &&
      savedBillingAddress.length &&
      savedShippingAddress &&
      savedShippingAddress.length &&
      userType === "loggedIn"
    ) {
      const defaultAddress = savedBillingAddress.find((addr) => addr.isDefault);
      const addressIdToUse =
        newBillingAddressId !== undefined
          ? newBillingAddressId
          : defaultAddress?.id;

      // Skip restoration if user explicitly selected "Add New Address" (reset or id === -1)
      const isUserSelectedNewAddress = 
        selectedBillingAddress === "reset" ||
        selectedShippingAddress === "reset" ||
        (typeof selectedBillingAddress !== "string" && selectedBillingAddress?.id === -1) ||
        (typeof selectedShippingAddress !== "string" && selectedShippingAddress?.id === -1);

      if (
        addressIdToUse !== undefined &&
        addressIdToUse !== "reset" &&
        typeof selectedBillingAddress !== "string" &&
        selectedBillingAddress?.id !== addressIdToUse &&
        !isUserSelectedNewAddress // Don't restore if user explicitly selected new address
      ) {
        handleSavedBillingAddressChange(
          "savedBillingAddress",
          addressIdToUse,
          updatedBillingAddresses
        );
        handleSavedShippingAddressChange(
          "savedShippingAddress",
          addressIdToUse,
          updatedShippingAddresses
        );
      }
    }

    // Ensure readonly is set for logged-in users when addresses exist
    if (userType === "loggedIn") {
      // Handle billing address readonly
      if (selectedBillingAddress && typeof selectedBillingAddress !== "string") {
        const addressId = Number(selectedBillingAddress.id);
        if (addressId > 0) {
          // Existing saved address - set readonly
          setIsBillingReadonly(true);
        } else if (addressId === 0 || addressId === -1) {
          // New address selected - make it editable
          setIsBillingReadonly(false);
        }
      } else if (savedBillingAddress && savedBillingAddress.length > 0) {
        // If no address is selected but addresses exist, set readonly (default address will be selected)
        if (selectedBillingAddress === null || typeof selectedBillingAddress === "string") {
          setIsBillingReadonly(true);
        }
      }
      
      // Handle shipping address readonly
      if (selectedShippingAddress === "reset") {
        // "Add New Address" was clicked - make it editable
        setIsShippingReadonly(false);
      } else if (selectedShippingAddress && typeof selectedShippingAddress !== "string") {
        const addressId = Number(selectedShippingAddress.id);
        if (addressId > 0) {
          // Existing saved address - set readonly
          setIsShippingReadonly(true);
        } else if (addressId === 0 || addressId === -1) {
          // New address selected - make it editable
          setIsShippingReadonly(false);
        }
      } else if (savedShippingAddress && savedShippingAddress.length > 0) {
        // If no address is selected but addresses exist, set readonly (default address will be selected)
        if (selectedShippingAddress === null || (typeof selectedShippingAddress === "string" && selectedShippingAddress !== "reset")) {
          setIsShippingReadonly(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedBillingAddress,
    savedShippingAddress,
    selectedBillingAddress,
    selectedShippingAddress,
    isBilling,
    cartData,
    userType,
    // Note: setIsBillingReadonly and setIsShippingReadonly are stable setters and don't need to be in deps
    // but we keep them for clarity. The real fix is the id comparison guards above.
  ]);


  const handleInputChange = useCallback((fieldName: string, value: string) => {
    fieldNameRef.current = fieldName;

    // Map shipping-specific field names to generic field names
    const fieldMap: Record<string, keyof intrfce.iAddressFormData> = {
      firstNameShiping: 'firstName',
      lastNameShiping: 'lastName',
      attnShiping: 'attn',
      contactShiping: 'contact',
      emailShiping: 'email',
      vemailShiping: 'vemail'
    };

    const mappedFieldName = fieldMap[fieldName];

    // Update shipping contact fields in formShippingAddress
    if (mappedFieldName) {
      setFormShippingAddress((prev) => ({ ...prev, [mappedFieldName]: value }));

      // If addresses are the same, also update billing to match shipping
      if (!isBilling) {
        setFormBillingAddress((prev) => ({ ...prev, [mappedFieldName]: value }));
      }
    }

    // Only validate non-address fields on change if they've been touched
    // Address fields will be validated on blur
    const addressFields = ['addressShipping', 'addressBilling', 'address'];
    if (!addressFields.includes(fieldName)) {
      // Always validate on change to clear errors when field becomes valid
      validateForm(fieldName, value, "shipping");
    }
    
    // Clear shippingAddressErr when user manually changes any shipping field
    if (validationErrors.shippingAddressErr && hasRestrictedShippingError.current && (
      fieldName === "firstNameShiping" || 
      fieldName === "lastNameShiping" || 
      fieldName === "attnShiping" ||
      fieldName === "contactShiping" ||
      fieldName === "emailShiping" ||
      fieldName === "vemailShiping"
    )) {
      console.log("Clearing shippingAddressErr - user manually changed field:", fieldName);
      setValidationErrors((prevErrors) => {
        const { shippingAddressErr, ...restErrors } = prevErrors;
        return restErrors;
      });
      setEnableReviewOrderBtn(true);
      hasRestrictedShippingError.current = false;
    }
  }, [validateForm, validationErrors, isBilling, setFormShippingAddress, setFormBillingAddress, setValidationErrors, setEnableReviewOrderBtn]);

  // Handle shipping field blur - mark as touched and validate
  const handleShippingFieldBlur = useCallback((fieldName: string, value: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName));
    // Use skipTouchedCheck since we know the field is being touched at this moment
    validateForm(fieldName, value, "shipping", true);
  }, [validateForm]);

  // Handler for address field blur - mark as touched and validate only on blur
  const handleAddressBlur = useCallback((fieldName: string, value: string, formType: "billing" | "shipping" | "") => {
    setTouchedFields((prev) => {
      const updated = new Set(prev);
      updated.add(fieldName);
      return updated;
    });
    // Use skipTouchedCheck since we know the field is being touched
    validateForm(fieldName, value, formType, true);
  }, [validateForm]);

  const shipingInfo = `shipingInfo visible`; // Shipping is always visible now (shown first)
  useEffect(() => {
    // This will be executed after the state has been updated
    // Skip validation for address fields as they are validated on blur
    // Only validate if field has been touched (user has interacted with it)
    let fieldName = fieldNameRef.current;
    const addressFields = ['addressShipping', 'addressBilling', 'address'];

    if (fieldName && !addressFields.includes(fieldName)) {
      // Only validate if field has been touched
      if (touchedFields.has(fieldName)) {
        // Map shipping-specific field names to generic field names for accessing formShippingAddress
        const fieldMap: Record<string, keyof intrfce.iAddressFormData> = {
          firstNameShiping: 'firstName',
          lastNameShiping: 'lastName',
          attnShiping: 'attn',
          contactShiping: 'contact',
          emailShiping: 'email',
          vemailShiping: 'vemail'
        };
        const mappedFieldName = fieldMap[fieldName] || fieldName;
        const value = formShippingAddress[mappedFieldName as keyof typeof formShippingAddress];
        if (value !== undefined) {
          validateForm(fieldName, value, "shipping");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formShippingAddress, touchedFields]);
  const handleCartChange = useCallback(async (
    productId?: string,
    selectedOption?: number,
    shippingOption?: string,
    coupon?: string,
    couponApplied?: boolean
  ) => {
    showCartLoader();
    if (productId && selectedOption !== undefined && selectedOption !== null) {
      await updateCart(
        handleSuccess,
        handleError,
        productId,
        selectedOption,
        shippingOption,
        undefined,
        undefined,
        false
      );
    } else if (shippingOption !== undefined && shippingOption !== null) {
      await updateCart(
        handleSuccess,
        handleError,
        undefined,
        undefined,
        shippingOption,
        undefined,
        undefined,
        false
      );
    } else {
      if (coupon) {
        await updateCart(
          handleSuccess,
          handleCouponError,
          undefined,
          undefined,
          undefined,
          coupon,
          undefined,
          false
        );
      } else {
        if (couponApplied) {
          await updateCart(
            handleSuccess,
            handleCouponError,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            couponApplied
          );
        }
      }
    }
    hideCartLoader();
  }, [showCartLoader, updateCart, handleSuccess, handleError, handleCouponError, hideCartLoader]);

  const handleCouponChange = useCallback((fieldName: string, promo: string | null) => {
    fieldNameRef.current = fieldName;
    setCoupon(promo);
    if (promo || promo?.length === 0) {
      setPromoErr(false);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: "",
      }));
    }
    console.log("promo==>", promo);
  }, []);

  const handleCouponDelete = useCallback((d: boolean) => {
    handleCartChange(undefined, undefined, undefined, undefined, d);
  }, [handleCartChange]);

  const handleCouponSubmit = useCallback((c: string) => {
    handleCartChange(undefined, undefined, undefined, c);
  }, [handleCartChange]);

  const handleisBillingChange = useCallback((
    e?: any,
    billingState?: boolean
  ) => {
    setIsBilling(billingState ?? !isBilling);
    sessionStorage.setItem("isBilling", JSON.stringify(!isBilling));
    setFormBillingAddress(formShippingAddress);
    if (
      isBilling &&
      savedBillingAddress &&
      savedBillingAddress.length &&
      !selectedBillingAddress &&
      userType === "loggedIn"
    ) {
      // Find the default address if available
      const defaultAddress =
        formShippingAddress && formShippingAddress.id
          ? formShippingAddress
          : savedBillingAddress.find((addr) => addr.isDefault);
      if (defaultAddress) {
        handleSavedBillingAddressChange(
          "savedBillingAddress",
          JSON.stringify(defaultAddress.id)
        );
      }
    } else {
      // When unchecking (billing different from shipping becomes false), 
      // just copy shipping to billing without modifying savedBillingAddress array
      let shippingAddressCopy: intrfce.iSavedAddress = {
        city: formShippingAddress.cityValue || "",
        countryName: formShippingAddress.countryName || "",
        country: formShippingAddress.countryValue || "",
        firstName: formShippingAddress.firstName || "",
        lastName: formShippingAddress.lastName || "",
        line1: formShippingAddress.address || "",
        line2: formShippingAddress.line2 || "",
        nickName: selectedShippingAddress && selectedShippingAddress !== "reset"
          ? selectedShippingAddress.nickName || ""
          : "",
        phoneNumber: formShippingAddress.contact || "",
        ...(formShippingAddress.id ? { id: Number(formShippingAddress.id) } : {}),
        ...(selectedShippingAddress !== "reset" && selectedShippingAddress?.id
          ? { id: Number(selectedShippingAddress.id) }
          : {}),
        postalCode: formShippingAddress.pincode || "",
        countrySubdivision: formShippingAddress.stateValue || "",
      };

      // Only set the selected address, don't modify the savedBillingAddress array
      setSelectedBillingAddress(shippingAddressCopy);
    }
  }, [isBilling, formShippingAddress, savedBillingAddress, selectedBillingAddress, userType, selectedShippingAddress, setIsBilling, setFormBillingAddress, handleSavedBillingAddressChange, setSelectedBillingAddress]);

  // Cart initialization is now handled by useCartOperations hook (useEffect inside the hook)

  const handleisBusinessPurchaseChange = useCallback((e: ChangeEvent<HTMLElement> | React.MouseEvent<HTMLButtonElement>) => {
    setIsBusinessPurchase(!isBusinessPurchase);
    setFormTaxData({
      companyName: '',
      vatNo: '',
    });
  }, [isBusinessPurchase]);
  const billaddr = useMemo(() => createBillingAddressObject(
    formBillingAddress,
    addressOptions,
    cartData?.emailAddress ?? null
  ), [formBillingAddress, addressOptions, cartData?.emailAddress]);

  const shipaddr = useMemo(() => createShippingAddressObject(
    isBilling,
    formShippingAddress,
    formBillingAddress,
    addressOptions,
    cartData?.emailAddress ?? null
  ), [isBilling, formShippingAddress, formBillingAddress, addressOptions, cartData?.emailAddress]);

  //const shipopt = shippingOptions?.shippingOption?.toString();
  // Get the translated shipping method description from shippingList
  const shippingMethodDescription = useMemo(() => 
    shippingList && shippingList.length > 0
      ? shippingList[0].description
      : undefined,
    [shippingList]
  );

  const taxExemptObj = useMemo(() => ({
    companyName: formTaxData.companyName,
    vatNo: formTaxData.vatNo,
  }), [formTaxData.companyName, formTaxData.vatNo]);
  // useEffect(() => {
  //   isBillingAddressValid();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [formBillingAddress]);
  // useEffect(() => {
  //   isBillingAddressValid();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [formShippingAddress]);

  useEffect(() => {
    setOrder((prevData) => ({
      ...prevData,
      cart: {
        futureUse: true,
        billingAddress: billaddr,
        shippingAddress: shipaddr,
        shippingMethod: shippingMethodDescription,
        taxExemption: taxExemptObj,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formBillingAddress,
    formShippingAddress,
    shippingOptions,
    formTaxData,
    shippingMethodDescription,
  ]);

  const handleShippingMethodChange = useCallback((fieldName: string, value: string) => {
    // Use the callback function to ensure the state is updated before performing other actions

    fieldNameRef.current = fieldName;
    setShippingOptions((prevShippingOptions) => {
      const newShippingOptions = { ...prevShippingOptions, [fieldName]: value };
      return newShippingOptions;
    });

    if (fieldName === "shippingOption") {
      handleCartChange(undefined, undefined, value);
    }
  }, [handleCartChange]);

  const handleCartQuantityChange = useCallback(async (
    productId: number,
    selectedOption: number
  ) => {
    let pid = productId.toString();
    handleCartChange(pid, selectedOption);
  }, [handleCartChange]);

  const addressRef = useRef<{
    formType: "billing" | "shipping" | null;
    data: intrfce.iAddressFormData | null;
  }>({
    formType: null,
    data: null,
  });

  // Handle user-initiated address changes from Places component
  const handleUserShippingAddressChange = useCallback(() => {
    if (validationErrors.shippingAddressErr && hasRestrictedShippingError.current) {
      console.log("Clearing shippingAddressErr - user changed address in Places component");
      setValidationErrors((prevErrors) => {
        const { shippingAddressErr, ...restErrors } = prevErrors;
        return restErrors;
      });
      setEnableReviewOrderBtn(true);
      hasRestrictedShippingError.current = false;
    }
  }, [validationErrors.shippingAddressErr]);

  const handleAddressUpdate = useCallback((data: any, formType: "billing" | "shipping") => {
    console.log("handleAddressUpdate===>", data, formType);

    addressRef.current = { formType, data };

    if (formType === "shipping") {
      // Don't clear error here - this callback triggers on ANY change including programmatic updates
      // Error clearing is handled in user-initiated events only (handleInputChange, handleSavedShippingAddressChange)

      // Check if this is an empty address update during restricted error state
      const isEmptyAddress = !data.address && !data.pincode && !data.cityValue;
      if (hasRestrictedShippingError.current && isEmptyAddress) {
        console.log("Empty address during error - using billing address data instead");
        // Instead of ignoring, use the billing address data we already set
        const billingAddressData = addressRef.current.data || formBillingAddress;
        if (billingAddressData && (billingAddressData.address || billingAddressData.pincode)) {
          // Don't overwrite - keep the billing address that was already set
          return;
        }
      }

      // Preserve existing contact fields when updating shipping address
      setFormShippingAddress((prev) => ({
        ...prev,
        ...data,
      }));

      // If addresses are the same, also update billing to match shipping
      if (!isBilling) {
        setFormBillingAddress((prev) => ({
          ...prev,
          ...data,
          // Preserve billing-specific fields
          email: prev.email || "",
          vemail: prev.vemail || "",
        }));
      }

      if (selectedAddressOption !== "recommendedAddress") {
        setTemporaryAddress({
          line1: data.address,
          line2: data.line2,
          city: data.cityValue,
          countrySubdivision: data.stateValue || "",
          postalCode: data.pincode,
          countryName: data.countryName,
          country: data.countryValue,
          firstName: formShippingAddress.firstName || "",
          lastName: formShippingAddress.lastName || "",
          phoneNumber: formShippingAddress.contact || "",
        });
      }
    } else {
      // Preserve existing contact fields when updating billing address
      setFormBillingAddress((prev) => ({
        ...prev,
        ...data,
      }));
      
      // Set temporary billing address when user edits (similar to shipping)
      if (isBilling && selectedBillingAddressOption !== "recommendedAddress") {
        setTemporaryBillingAddress({
          line1: data.address,
          line2: data.line2,
          city: data.cityValue,
          countrySubdivision: data.stateValue || "",
          postalCode: data.pincode,
          countryName: data.countryName,
          country: data.countryValue,
          firstName: formBillingAddress.firstName || "",
          lastName: formBillingAddress.lastName || "",
          phoneNumber: formBillingAddress.contact || "",
        });
      }
    }
  }, [formBillingAddress, isBilling, selectedAddressOption, selectedBillingAddressOption, setFormShippingAddress, setTemporaryAddress, setTemporaryBillingAddress, setFormBillingAddress, formShippingAddress.contact, formShippingAddress.firstName, formShippingAddress.lastName]);

  const handleCartSuccess = useCallback((res) => {
    if (res?.paymentSession?.id) {
      sessionStorage.setItem("paymentSession", res?.paymentSession?.id);
    }
    setTemporaryAddress(null);
    if (userType) {
      showBodyLoader();
      reviewOrder(userType);      
    }
  }, [userType, showBodyLoader, reviewOrder, setTemporaryAddress]);

  const handleCartError = useCallback((error: any) => {
    hideBodyLoader();

    // Navigate back to Step 1 for any error
    setCurrentStep(1);
    sessionStorage.removeItem('proceedToStep2');

    // Handle error object
    if (error && error.code === "postal_code_invalid") {
      console.error("Postal code invalid:", error.message);
      // Add custom logic for invalid postal code error here (e.g., show specific UI message)
      setAddressErr(error?.description); // Example usage
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        addressErr: error?.description,
      }));
    } else if (error && error.code === 'restricted-ship-to-country') {
      console.log('🚨 Restricted ship-to-country error:', error);
      setShowAddressModal(false);
      
      // Set error state refs to prevent premature clearing
      hasRestrictedShippingError.current = true;
      skipAddressAutoLoad.current = true;
      
      setEnableReviewOrderBtn(false);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        shippingAddressErr: t('ADDRESS_NOT_SHIPPABLE'),
      }));

      // Set selectedShippingAddress to "ADD_NEW_ADDRESS" (-1) to prevent default address from loading
      // This happens ONLY for restricted-ship-to-country error
      // Always populate with current billing/shipping address data
      if (userType === "loggedIn") {
        const currentShippingAddress = !isBilling ? formBillingAddress : formShippingAddress;
        
        const addNewAddressWithData: intrfce.iSavedAddress = {
          id: -1,
          city: currentShippingAddress.cityValue || "",
          country: currentShippingAddress.countryValue || "",
          countryName: currentShippingAddress.countryName || "",
          countrySubdivision: getStateValue(currentShippingAddress.stateValue, "") || "",
          county: countyField ? (currentShippingAddress.county || "") : undefined,
          firstName: !isBilling 
            ? formBillingAddress.firstName || ""
            : formShippingAddress.firstName || "",
          isDefault: false,
          lastName: !isBilling 
            ? formBillingAddress.lastName || ""
            : formShippingAddress.lastName || "",
          line1: currentShippingAddress.address || "",
          line2: currentShippingAddress.line2 || "",
          nickName: t("ADD_NEW_ADDRESS"),
          phoneNumber: !isBilling 
            ? formBillingAddress.contact || ""
            : formShippingAddress.contact || "",
          postalCode: currentShippingAddress.pincode || "",
        };
        
        console.log("Setting selectedShippingAddress with billing data:", addNewAddressWithData);
        setSelectedShippingAddress(addNewAddressWithData);
      }
      
      // Ensure shipping form is editable
      setIsBillingReadonly(false);
      
      // Reset skip flag after state updates complete (longer timeout to ensure all state updates finish)
      setTimeout(() => {
        skipAddressAutoLoad.current = false;
      }, 500);
    } else if (error && error.message) {
      // Handle other errors
      emptyCart(error.message);
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideBodyLoader, emptyCart, t, isBilling, formBillingAddress, formShippingAddress, userType, stateDropdown, locale, countyField, setIsBilling, setFormShippingAddress, setSelectedShippingAddress, setIsBillingReadonly, setShowAddressModal, setEnableReviewOrderBtn, setValidationErrors, setAddressErr, setCurrentStep]);

  // Update saved address arrays with actual form data for addresses with id === -1
  const updateSavedAddressesBeforeSubmit = useCallback((billaddr: any, shipaddr: any) => {
    // Update savedBillingAddress if it has an object with id === -1
    if (savedBillingAddress && savedBillingAddress.length > 0) {
      const hasNewBillingAddress = savedBillingAddress.some(addr => addr.id === -1);
      if (hasNewBillingAddress) {
        setSavedBillingAddress(updateAddressWithData(
          savedBillingAddress,
          billaddr,
          t("ADD_NEW_ADDRESS")
        ));
      }
    }

    // Update savedShippingAddress if it has an object with id === -1
    if (savedShippingAddress && savedShippingAddress.length > 0 && isBilling) {
      const hasNewShippingAddress = savedShippingAddress.some(addr => addr.id === -1);
      if (hasNewShippingAddress) {
        setSavedShippingAddress(updateAddressWithData(
          savedShippingAddress,
          shipaddr,
          t("ADD_NEW_ADDRESS")
        ));
      }
    }
  }, [savedBillingAddress, savedShippingAddress, isBilling, t, setSavedBillingAddress, setSavedShippingAddress]);

  const handleSubmitCart = useCallback(async (orderDet) => {
    let user: string | null = sessionStorage.getItem("customer");
    if (user && orderDet) {
      sessionStorage.setItem(user, JSON.stringify(orderDet));
      sessionStorage.setItem("isBilling", JSON.stringify(isBilling));
    }
    console.log("orderDet==>", orderDet);
    showBodyLoader();
    await submitCart(
        { ...orderDet, locale: locale, brand: brand },
        handleCartSuccess,
        handleCartError
    );
  }, [isBilling, showBodyLoader, submitCart, locale, brand, handleCartSuccess, handleCartError]);

  const submitCustomerForm = useCallback(() => {
    if (isBillingFormValid() && isBillingAddressValid() && isTaxDataValid()) {
      if (isBilling) {
        if (!isBillingFormValid() || !isBillingAddressValid()) {
          console.log(isBilling, formShippingAddress);
          return false; // Exit the function immediately if any field is invalid
        }
      }
      if (order && order?.cart && Object.keys(order?.cart).length === 0) {
        const billaddr = createBillingAddressObject(
          formBillingAddress,
          addressOptions,
          cartData?.emailAddress ?? null
        );
        const shipaddr = createShippingAddressObject(
          isBilling,
          formShippingAddress,
          formBillingAddress,
          addressOptions,
          cartData?.emailAddress ?? null
        );
        //const shipopt = shippingOptions?.shippingOption?.toString();
        const taxExempt = {
          companyName: formTaxData.companyName,
          vatNo: formTaxData.vatNo
        };
        const updatedBillingAddress = billaddr; // Default to cartData's billingAddress
        const updatedShippingAddress = shipaddr; // Default to cartData's billingAddress
        // If cartData.billingAddress.emailAddress is null or empty, use cartData.emailAddress
        if (updatedBillingAddress && !updatedBillingAddress?.emailAddress) {
          updatedBillingAddress.emailAddress =
            cartData?.emailAddress || updatedBillingAddress.emailAddress;
        }
        if (updatedShippingAddress && !updatedShippingAddress?.emailAddress) {
          updatedShippingAddress.emailAddress =
            cartData?.emailAddress || updatedShippingAddress.emailAddress;
        }
        let cartPayload = {
          cart: {
            futureUse: futureUse,
            billingAddress: updatedBillingAddress,
            shippingAddress: updatedShippingAddress,
            shippingMethod: shippingMethodDescription,
            taxExemption: taxExempt,
          },
        };
        if (userType === "guest") {
          // Update saved addresses before submitting
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(cartPayload);
        } else {
          // Update saved addresses before submitting
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(cartPayload);
        }
      } else {
        if (userType === "guest") {
          // Update saved addresses before submitting (using billaddr and shipaddr from the global scope)
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(order);
        } else {
          // Update saved addresses before submitting (using billaddr and shipaddr from the global scope)
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(order);
        }
      }
    } else {
      console.log("Form has found missing field values");
      return false;
    }
  }, [isBillingFormValid, isBillingAddressValid, isTaxDataValid, isBilling, formShippingAddress, order, formBillingAddress, addressOptions, cartData?.emailAddress, formTaxData, futureUse, shippingMethodDescription, userType, billaddr, shipaddr, updateSavedAddressesBeforeSubmit, handleSubmitCart]);
  
  // Address operations are now provided by useAddressOperations hook
  // updateRecommendedAddress is now provided by the hook

  // handleDualAddressSuccess moved above (before hook calls)

  // Address modal handlers are now provided by useAddressOperations hook

  const handleFormSubmit = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    const sessionexpiredID = nvCookieHelpers.getCookie("session_expired");
    if (sessionexpiredID && !JSON.parse(sessionexpiredID)) {
      //submitCustomerForm();
      let isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
      if (isLoggedIn && JSON.parse(isLoggedIn)) {
        submitCustomerForm();
      } else {
        if (savePayment) {
          submitCustomerForm();
        } else {
          if (savePayment || validPayment) {
            if (!savePayment) {
              setValidationErrors((prevErrors) => ({
                ...prevErrors,
                paymentSrc: "Please retry with a different payment method",
              }));
            }
            if (validPayment) {
              setValidationErrors((prevErrors) => ({
                ...prevErrors,
                paymentSrc: "",
              }));
              setSavePayment(true);
            }
          } else {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              paymentSrc: "Please retry with a different payment method",
            }));
            ev.preventDefault();
            return;
          }
        }
      }
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
  }, [submitCustomerForm, emptyCart, t, savePayment, validPayment, setValidationErrors, setSavePayment]);


  return (
    <div className="User">
      <Container>
        <Form className="orderAddressDetails" autoComplete="none" aria-labelledby="orderAddressDetails">
          <Row xs={"auto"} md={"auto"}>
            <Col md={"7"} sm={"auto"} className="w100" role="region" aria-labelledby="cartSectionHeader">
              <Card id="ShoppingCartMobile">
                <div className={isCartLoading ? `cart-loading` : ""} aria-busy={isCartLoading}>
                  {isCartLoading && (
                    <Image
                      src={cartLoader}
                      className="cartLoader"
                      alt="Loading cart"
                    ></Image>
                  )}
                  {cartData && (
                    <Cart
                      onCartChange={handleCartQuantityChange}
                      validationErrors={validationErrors}
                      onPromoChange={handleCouponChange}
                      onPromoSubmit={handleCouponSubmit}
                      onPromoDelete={handleCouponDelete}
                      isThanks={isThanks}
                      isVerify={isVerify}
                      cartData={cartData}
                      promoCodeError={promoErr}
                    />
                  )}
                </div>
              </Card>
              <div id="newLoginContainer">
                {userType === "loggedIn" && <h1 id="orderAddressDetails">{t("VERIFY_ORDER_HEADER")}</h1>}
                {userType === "guest" && <><h1 id="orderAddressDetails">{t("CHECKOUT_GUEST_BTN")}</h1>
                  <p className="dr_loginInstructions">
                    {t("HAVE_AN_ACCOUNT")}{" "}
                    <Button className="continue" onClick={handleLogin}>
                      {t("SITE_LOGIN_PAGE_TITLE")}
                    </Button>
                  </p></>
                }
              </div>
              <h3 id="cartSectionHeader" className="visually-hidden">{t("SHOPPING_CART_HEADER")}</h3>
              {addressErr && (
                <>
                  <p
                    className={`addressErr ${validationErrors.addressErr ? "is-invalid" : ""
                      }`}
                  ></p>
                  <Form.Control.Feedback type="invalid" id="addressErr" aria-live="polite" aria-describedby="addressErr">
                    {validationErrors.addressErr}
                  </Form.Control.Feedback>
                </>
              )}
              
              {/* STEP 1: Address Information */}
              {currentStep === 1 && (
                <>
                  {/* Shipping Address Section - Always shown first */}
                  <Row xs={"auto"} md={"auto"} className={shipingInfo} id="shippingDetailsHeader" role="region" aria-labelledby="shippingDetailsHeader" aria-describedby="shippingDetails">
                    <Col md={12} className="p-0">
                      <h3 id="shippingDetailsHeader" role="region" aria-labelledby="shippingDetailsHeader" aria-describedby="shippingDetails" className={`shippingDetails ${validationErrors.shippingAddressErr ? "is-invalid" : ""}`}>{t("SHIPPING_INFORMATION")}</h3>
                      <p className="instructions" id="shippingDetails" role="region" aria-labelledby="shippingDetails" aria-describedby="shippingDetails">{t("REQUIRED_FIELDS_TEXT")}</p>
                      {validationErrors.shippingAddressErr && (
                        <div className="invalid-feedback d-block" id="shippingAddressErr" role="alert" aria-live="polite">
                          {validationErrors.shippingAddressErr}
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Row xs={"auto"} md={"auto"} id="shippingDetailsForm" role="region" aria-labelledby="shippingDetailsForm" aria-describedby="shippingDetailsForm">
                    <Col md={6}>
                      {userType === "loggedIn" &&
                        savedShippingAddress &&
                        savedShippingAddress.length > 0 && (
                          <Form.Group controlId="savedShippingAddress" id="shippingAddressSelect" role="region" aria-labelledby="shippingAddressSelect" aria-describedby="shippingAddressSelect">
                            <Form.Select
                              autoComplete="none"
                              value={
                                selectedShippingAddress
                                  ? selectedShippingAddress["id"]
                                  : ""
                              }
                              name="savedShippingAddress"
                              aria-label="Select Shipping Address"
                              onChange={(e) =>
                                handleSavedShippingAddressChange(
                                  "savedShippingAddress",
                                  e.target.value
                                )
                              }
                              aria-invalid={!!validationErrors.savedShippingAddress}
                              aria-describedby={validationErrors.savedShippingAddress ? "savedShippingAddressError" : undefined}
                              isInvalid={!!validationErrors.savedShippingAddress}
                            >
                              {savedShippingAddress?.map((address, index) => (
                                <option
                                  key={index}
                                  value={address["id"]}
                                  selected={address["isDefault"]}
                                >
                                  {address["nickName"]}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid" id="savedShippingAddressError" aria-live="polite" aria-describedby="savedShippingAddressError">
                              {validationErrors.savedShippingAddress}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                    </Col>
                    {userType === "loggedIn" &&
                      savedShippingAddress &&
                      savedShippingAddress.length > 0 && (
                        <Col md={6}>
                          <Anchor
                            className="pull-right"
                            onClick={() =>
                              handleSavedShippingAddressChange(
                                "savedShippingAddress",
                                RESET_ADDRESS
                              )
                            }
                            aria-label="Add new shipping address"
                            aria-describedby="shippingAddressSelect"
                          >
                            <FaPlus />&#160;{t("ADD_NEW_ADDRESS")}
                          </Anchor>
                        </Col>
                      )}
                  </Row>
                  <Row xs={"auto"} md={"auto"} className="mb-4">
                    <div className="Shipping" id="shippingDetailsForm" role="region" aria-labelledby="shippingDetailsForm" aria-describedby="shippingDetailsForm">
                      {locale === 'ja-jp' && (
                        <Col md={6}>
                          <Form.Group controlId="lastNameShiping">
                            <Form.Label htmlFor="lastNameShiping" className="visually-hidden">{t("LAST_NAME")}</Form.Label>
                            <Form.Control
                              id="lastNameShiping"
                              className={`form-control ${isShippingReadonly ? "disabled" : ""
                                }`}
                              required={true}
                              readOnly={isShippingReadonly}
                              placeholder={t("LAST_NAME_REQUIRED")}
                              type="text"
                              autoComplete="none"
                              aria-required="true"
                              aria-invalid={!!validationErrors.lastNameShiping}
                              aria-describedby={validationErrors.lastNameShiping ? "lastNameShipingError" : undefined}
                              isInvalid={!!validationErrors.lastNameShiping}
                              name="lastNameShiping"
                              value={formShippingAddress.lastName || ""}
                              onBlur={(e) =>
                                handleShippingFieldBlur(
                                  "lastNameShiping",
                                  e.target.value
                                )
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  "lastNameShiping",
                                  e.target.value
                                )
                              }
                            />
                            <Form.Control.Feedback type="invalid" id="lastNameShipingError" aria-live="polite" aria-describedby="lastNameShipingError">
                              {validationErrors.lastNameShiping}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={6}>
                        <Form.Group controlId="firstNameShiping">
                          <Form.Label htmlFor="firstNameShiping" className="visually-hidden">{t("FIRST_NAME")}</Form.Label>
                          <Form.Control
                            id="firstNameShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={true}
                            readOnly={isShippingReadonly}
                            placeholder={t("FIRST_NAME_REQUIRED")}
                            type="text"
                            autoComplete="none"
                            aria-required="true"
                            aria-invalid={!!validationErrors.firstNameShiping}
                            aria-describedby={validationErrors.firstNameShiping ? "firstNameShipingError" : undefined}
                            isInvalid={!!validationErrors.firstNameShiping}
                            name="firstNameShiping"
                            value={formShippingAddress.firstName || ""}
                            onBlur={(e) =>
                              handleShippingFieldBlur(
                                "firstNameShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "firstNameShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="firstNameShipingError" aria-live="polite" aria-describedby="firstNameShipingError">
                            {validationErrors.firstNameShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {locale !== 'ja-jp' && (<Col md={6}>
                        <Form.Group controlId="lastNameShiping">
                          <Form.Label htmlFor="lastNameShiping" className="visually-hidden">{t("LAST_NAME")}</Form.Label>
                          <Form.Control
                            id="lastNameShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={true}
                            readOnly={isShippingReadonly}
                            placeholder={t("LAST_NAME_REQUIRED")}
                            type="text"
                            autoComplete="none"
                            aria-required="true"
                            aria-invalid={!!validationErrors.lastNameShiping}
                            aria-describedby={validationErrors.lastNameShiping ? "lastNameShipingError" : undefined}
                            isInvalid={!!validationErrors.lastNameShiping}
                            name="lastNameShiping"
                            value={formShippingAddress.lastName || ""}
                            onBlur={(e) =>
                              handleShippingFieldBlur(
                                "lastNameShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "lastNameShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="lastNameShipingError" aria-live="polite" aria-describedby="lastNameShipingError">
                            {validationErrors.lastNameShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>)}
                      <Col md={6}>
                        <Form.Group controlId="attnShiping">
                          <Form.Label htmlFor="attnShiping" className="visually-hidden">{t("ATTN") || "Attn."}</Form.Label>
                          <Form.Control
                            id="attnShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={false}
                            readOnly={isShippingReadonly}
                            placeholder={t("ATTN") || "Attn."}
                            type="text"
                            autoComplete="none"
                            aria-required="false"
                            aria-invalid={!!validationErrors.attnShiping}
                            aria-describedby={validationErrors.attnShiping ? "attnShipingError" : undefined}
                            isInvalid={!!validationErrors.attnShiping}
                            name="attnShiping"
                            value={formShippingAddress.attn || ""}
                            onBlur={(e) =>
                              handleShippingFieldBlur(
                                "attnShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "attnShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="attnShipingError" aria-live="polite" aria-describedby="attnShipingError">
                            {validationErrors.attnShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="contactShiping">
                          <Form.Label htmlFor="contactShiping" className="visually-hidden">{t("PHONE_NUMBER")}</Form.Label>
                          <Form.Control
                            id="contactShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={true}
                            readOnly={isShippingReadonly}
                            placeholder={t("PHONE_NUMBER_REQUIRED")}
                            type="text"
                            autoComplete="none"
                            aria-required="true"
                            aria-invalid={!!validationErrors.contactShiping}
                            aria-describedby={validationErrors.contactShiping ? "contactShipingError" : undefined}
                            isInvalid={!!validationErrors.contactShiping}
                            name="contactShiping"
                            value={formatPhoneNo(formShippingAddress.contact || "")}
                            onBlur={(e) =>
                              handleShippingFieldBlur(
                                "contactShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "contactShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="contactShipingError" aria-live="polite" aria-describedby="contactShipingError">
                            {validationErrors.contactShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {userType !== 'loggedIn' && (
                        <>
                          <Col md={6}>
                            <Form.Group controlId="emailShiping">
                              <Form.Label htmlFor="emailShiping" className="visually-hidden">{t("EMAIL_ADDRESS_COLON")}</Form.Label>
                              <Form.Control
                                id="emailShiping"
                                className={`form-control ${isShippingReadonly ? "disabled" : ""
                                  }`}
                                required={true}
                                readOnly={isShippingReadonly}
                                placeholder={t("EMAIL_ADDRESS_REQUIRED")}
                                type="email"
                                autoComplete="none"
                                aria-required="true"
                                aria-invalid={!!validationErrors.emailShiping}
                                aria-describedby={validationErrors.emailShiping ? "emailShipingError" : undefined}
                                isInvalid={!!validationErrors.emailShiping}
                                name="emailShiping"
                                value={formShippingAddress.email || ""}
                                onBlur={(e) =>
                                  handleShippingFieldBlur(
                                    "emailShiping",
                                    e.target.value
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "emailShiping",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control.Feedback type="invalid" id="emailShipingError" aria-live="polite" aria-describedby="emailShipingError">
                                {validationErrors.emailShiping}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="vemailShiping">
                              <Form.Label htmlFor="vemailShiping" className="visually-hidden">{t("VERIFY_EMAIL_ADDRESS_COLON")}</Form.Label>
                              <Form.Control
                                id="vemailShiping"
                                className={`form-control ${isShippingReadonly ? "disabled" : ""
                                  }`}
                                required={true}
                                readOnly={isShippingReadonly}
                                placeholder={t("VERIFY_EMAIL_ADDRESS_REQUIRED")}
                                type="email"
                                autoComplete="none"
                                aria-required="true"
                                aria-invalid={!!validationErrors.vemailShiping}
                                aria-describedby={validationErrors.vemailShiping ? "vemailShipingError" : undefined}
                                isInvalid={!!validationErrors.vemailShiping}
                                name="vemailShiping"
                                value={formShippingAddress.vemail || ""}
                                onBlur={(e) =>
                                  handleShippingFieldBlur(
                                    "vemailShiping",
                                    e.target.value
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "vemailShiping",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control.Feedback type="invalid" id="vemailShipingError" aria-live="polite" aria-describedby="vemailShipingError">
                                {validationErrors.vemailShiping}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </>
                      )}
                    </div>
                    {locale !== 'ja-jp' ? (<Places
                      isThanks={isThanks}
                      isVerify={isVerify}
                      validationErrors={validationErrors}
                      isBillingReadonly={isShippingReadonly}
                      billingAddr={
                        selectedShippingAddress
                          ? JSON.stringify(selectedShippingAddress)
                          : ""
                      }
                      formType="shipping"
                      isShippingReadonly={isShippingReadonly}
                      onPlaceUpdate={(data) =>
                        handleAddressUpdate(data, "shipping")
                      }
                      onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "shipping")}
                      onUserAddressChange={handleUserShippingAddressChange}
                      brand={brand}
                    />
                    ) : (
                      <PlacesJapan
                        isThanks={isThanks}
                        isVerify={isVerify}
                        validationErrors={validationErrors}
                          isBillingReadonly={isShippingReadonly}
                          billingAddr={
                            selectedShippingAddress
                              ? JSON.stringify(selectedShippingAddress)
                              : ""
                          }
                          formType="shipping"
                        isShippingReadonly={isShippingReadonly}
                        onPlaceUpdate={(data) =>
                          handleAddressUpdate(data, "shipping")
                        }
                        onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "shipping")}
                        onUserAddressChange={handleUserShippingAddressChange}
                      />
                    )}
                  </Row>

                  {/* Checkbox: Billing address different from shipping */}
                  <Row xs={"auto"} md={"auto"} className="mb-4">
                    <Col md={12}>
                      <Form.Group className="isBilling" controlId="isBilling">
                        <Form.Check
                          type="checkbox"
                          name="sameBilling"
                          label={t("BILLING_ADDRESS_DIFFERENT_SHIPPING")}
                          checked={isBilling}
                          onChange={handleisBillingChange}
                          aria-checked={isBilling}
                          aria-label={t("BILLING_ADDRESS_DIFFERENT_SHIPPING")}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Billing Address Section - Shown when checkbox is checked */}
                  {isBilling && (
                    <>
                      <Row xs={"auto"} md={"auto"} className="mb-2" role="region" aria-labelledby="billingDetailsHeader">
                        <Col md={12} className="p-0">
                          <h3
                            id="billingDetailsHeader"
                            className={`billingDetails ${validationErrors.register ? "is-invalid" : ""}`} >
                            {t("BILLING_ADDRESS")}
                          </h3>
                          <p className="instructions" id="billingDetails" role="region" aria-labelledby="billingDetailsHeader" aria-describedby="billingDetails">Please enter your billing information if it is different from your shipping address.</p>
                          <p className="instructions" id="billingDetails" role="region" aria-labelledby="billingDetailsHeader" aria-describedby="billingDetails">{t("REQUIRED_FIELDS_TEXT")}</p>
                          <Form.Control.Feedback type="invalid" id="billingDetailsError" aria-live="polite">
                            {validationErrors.register}
                          </Form.Control.Feedback>
                        </Col>
                      </Row>
                      <Row xs={"auto"} md={"auto"} className=" mb-2" role="region" aria-labelledby="billingDetailsForm" id="billingDetailsForm">
                        <Col md={6}>
                          {userType === "loggedIn" &&
                            savedBillingAddress &&
                            savedBillingAddress.length > 0 ? (
                            <Form.Group controlId="savedBillingAddress" id="billingAddressSelect" aria-label="Saved Billing Address" role="region" aria-labelledby="billingAddressSelect">
                              {/* Add a visible label for the select, or use aria-label if not possible */}
                              <label htmlFor="savedBillingAddressSelect" className="visually-hidden">
                                Select Billing Address
                              </label>
                              <Form.Select
                                id="savedBillingAddressSelect"
                                autoComplete="none"
                                value={selectedBillingAddress ? selectedBillingAddress["id"] : ""}
                                name="savedBillingAddress"
                                aria-label="Select Billing Address" // use only if no visible label
                                aria-required="true" // if this is a required field
                                onChange={(e) =>
                                  handleSavedBillingAddressChange(
                                    "savedBillingAddress",
                                    e.target.value
                                  )
                                }
                              >
                                {savedBillingAddress?.map((address, index) => (
                                  <option
                                    key={address["id"] ?? index}
                                    value={address["id"]}
                                    selected={address["isDefault"]}
                                    aria-selected={address["isDefault"]}
                                    aria-label={address["nickName"]}
                                    aria-describedby="billingAddressSelect"
                                  // Remove selected, React handles it via value on <select>
                                  >
                                    {address["nickName"]}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          ) : null}
                        </Col>
                        {userType === "loggedIn" &&
                          savedBillingAddress &&
                          savedBillingAddress.length ? (
                          <Col md={6}>
                            <Anchor
                              className="pull-right"
                              onClick={() =>
                                handleSavedBillingAddressChange(
                                  "savedBillingAddress",
                                  RESET_ADDRESS
                                )
                              }
                              aria-label="Add new address"
                              aria-describedby="addNewAddress"
                              aria-live="polite"
                            >
                              <FaPlus />&#160;{t("ADD_NEW_ADDRESS")}
                            </Anchor>
                            <span id="addNewAddress" className="visually-hidden">
                              {t("ADD_NEW_ADDRESS_DESCRIPTION") || "Add a new billing address to your account"}
                            </span>
                          </Col>
                        ) : null}
                      </Row>
                      <Row xs={"auto"} md={"auto"} className="mb-4">
                        {locale !== 'ja-jp' ? (
                          <>
                            <Billing
                              userType={userType}
                              onInputChange={handleBillingInputChange}
                              onFieldBlur={handleBillingFieldBlur}
                              formBillingAddress={formBillingAddress}
                              validationErrors={validationErrors}
                              isReadOnly={isBillingReadonly}
                              billingUser={
                                selectedBillingAddress
                                  ? JSON.stringify(selectedBillingAddress)
                                  : ""
                              }
                            />
                            <Places
                              isThanks={isThanks}
                              isVerify={isVerify}
                              validationErrors={validationErrors}
                              isBillingReadonly={isBillingReadonly}
                              billingAddr={
                                selectedBillingAddress
                                  ? JSON.stringify(selectedBillingAddress)
                                  : ""
                              }
                              formType="billing"
                              isShippingReadonly={isBillingReadonly}
                              onPlaceUpdate={(data) => handleAddressUpdate(data, "billing")}
                              onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "billing")}
                              brand={brand}
                            />
                          </>
                        ) : (
                          <>
                            <BillingJapan
                              userType={userType}
                              onInputChange={handleBillingInputChange}
                              onFieldBlur={handleBillingFieldBlur}
                              formBillingAddress={formBillingAddress}
                              validationErrors={validationErrors}
                              isReadOnly={isBillingReadonly}
                              billingUser={
                                selectedBillingAddress
                                  ? JSON.stringify(selectedBillingAddress)
                                  : ""
                              }
                            />
                            <PlacesJapan
                              isThanks={isThanks}
                              isVerify={isVerify}
                              validationErrors={validationErrors}
                              isBillingReadonly={isBillingReadonly}
                              billingAddr={
                                selectedBillingAddress
                                  ? JSON.stringify(selectedBillingAddress)
                                  : ""
                              }
                              formType="billing"
                              isShippingReadonly={isBillingReadonly}
                              onPlaceUpdate={(data) => handleAddressUpdate(data, "billing")}
                              onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "billing")}
                            />
                          </>
                        )}
                      </Row>
                </>
              )}
                  {locale !== 'en-us' && (
                    <BusinessPurchaseSection
                      isBusinessPurchase={isBusinessPurchase}
                      formTaxData={formTaxData}
                      validationErrors={validationErrors}
                      handleisBusinessPurchaseChange={handleisBusinessPurchaseChange}
                      setFormTaxData={setFormTaxData}
                      t={t}
                    />
                  )}
              
              {/* Step 1 Navigation */}
              <Row xs={"auto"} md={"auto"} className="mb-4">
                <Col md={12} className="d-flex justify-content-end">
                      <Button
                        type="button"
                        variant="success"
                        size="lg"
                        className="btn-green continue-button"
                        onClick={handleContinueToPayment}
                        disabled={!isContinueButtonEnabled}
                        aria-label={t("CONTINUE_BTN")}
                      >
                        {t('CONTINUE_BTN')}
                      </Button>
                </Col>
              </Row>
              </>
              )}
              
              {/* STEP 2: Payment & Shipping Method */}
              {currentStep === 2 && (
                <CheckoutStep2
                  cartData={cartData}
                  shippingList={shippingList}
                  shippingOptions={shippingOptions}
                  validationErrors={validationErrors}
                  handleShippingMethodChange={handleShippingMethodChange}
                  selectedBillingAddress={selectedBillingAddress}
                  savePayment={savePayment}
                  formBillingAddress={formBillingAddress}
                  handleFormSubmit={handleFormSubmit}
                  enableReviewOrderBtn={enableReviewOrderBtn}
                  handleBackToAddresses={handleBackToAddresses}
                />
              )}
            </Col>
            <Col md={"5"} sm={"auto"} className="w100">
              <Card id="ShoppingCart">
                <div className={isCartLoading ? `cart-loading` : ""}>
                  {isCartLoading && (
                    <Image
                      src={cartLoader}
                      className="cartLoader"
                      alt="CartLoader"
                    ></Image>
                  )}
                  {cartData && (
                    <Cart
                      onCartChange={handleCartQuantityChange}
                      validationErrors={validationErrors}
                      onPromoChange={handleCouponChange}
                      onPromoSubmit={handleCouponSubmit}
                      onPromoDelete={handleCouponDelete}
                      isThanks={isThanks}
                      isVerify={isVerify}
                      cartData={cartData}
                      promoCodeError={promoErr}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
      {showAddressModal && (
        <AddressModal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          selectedAddressOption={selectedAddressOption}
          temporaryAddress={temporaryAddress}
          recommendedShippingAddress={recommendedShippingAddress}
          addressValidationResult={shippingValidationResult}
          formShippingAddress={formShippingAddress}
          onAddressOptionChange={handleAddressOptionChange}
          onShippingAddressUpdate={handleShippingAddressUpdate}
          onConfirmAddress={handleConfirmAddress}
          // Dual address props
          isBilling={isBilling}
          temporaryBillingAddress={temporaryBillingAddress}
          recommendedBillingAddress={recommendedBillingAddress}
          selectedBillingAddressOption={selectedBillingAddressOption}
          formBillingAddress={formBillingAddress}
          onBillingAddressOptionChange={(e, option) => setSelectedBillingAddressOption(option as "yourAddress" | "recommendedAddress")}
          billingValidationResult={billingValidationResult}
          shippingValidationResult={shippingValidationResult}
          // Differences for highlighting
          billingAddressDifferences={billingAddressDifferences}
          shippingAddressDifferences={shippingAddressDifferences}
          // Brand and validation errors
          brand={brand}
          validationErrors={validationErrors}
        />
      )}
    </div>
  );
};
export default User;
